import React from 'react';
import { Trophy, Coins, TrendingUp } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const Leaderboard = ({ leaderboard, sessionId, isMobile }) => {
    const { theme } = useTheme();

    if (!leaderboard) return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '24px' : '34px' }}>
            <div style={{ textAlign: 'center', animation: 'bubbleAppear 1s ease-in-out' }}>
                <h2 style={{
                    color: theme.gold,
                    fontWeight: 'bolder',
                    textTransform: 'uppercase',
                    fontSize: isMobile ? '35px' : '51px',
                    margin: '0 0 10px 0'
                }}>
                    Leaderboard
                </h2>
                <p style={{
                    color: theme.accent,
                    fontWeight: 'bold',
                    fontSize: isMobile ? '20px' : '30px',
                    margin: '0'
                }}>
                    Top Players
                </p>
            </div>

            {/* Most Wins */}
            <div style={{
                backgroundColor: theme.panelBg,
                borderRadius: '10px',
                boxShadow: '0 4px 7px rgba(40, 40, 40, 1)',
                animation: 'bubbleAppear 0.5s ease-in-out'
            }}>
                <div style={{
                    padding: isMobile ? '17px' : '26px',
                    borderBottom: `3px solid ${theme.accent}`,
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px'
                }}>
                    <Trophy size={isMobile ? 30 : 40} style={{ color: theme.accent }} />
                    <h3 style={{
                        color: theme.gold,
                        fontWeight: 'bolder',
                        textTransform: 'uppercase',
                        margin: '0',
                        fontSize: isMobile ? '24px' : '32px'
                    }}>
                        Most Wins
                    </h3>
                </div>
                <div style={{ padding: isMobile ? '17px' : '26px' }}>
                    {leaderboard.top_by_wins.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            color: theme.accent,
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            padding: isMobile ? '20px' : '30px',
                            fontSize: isMobile ? '18px' : '20px'
                        }}>
                            No data yet
                        </div>
                    ) : (
                        leaderboard.top_by_wins.map(entry => (
                            <LeaderboardRow
                                key={entry.session_id}
                                entry={entry}
                                sessionId={sessionId}
                                theme={theme}
                                isMobile={isMobile}
                                value={entry.wins ? `${entry.wins} Wins` : ''}
                            />
                        ))
                    )}
                </div>
            </div>

            {/* Highest Balance */}
            <div style={{
                backgroundColor: theme.panelBg,
                borderRadius: '10px',
                boxShadow: '0 4px 7px rgba(40, 40, 40, 1)',
                animation: 'bubbleAppear 0.6s ease-in-out'
            }}>
                <div style={{
                    padding: isMobile ? '17px' : '26px',
                    borderBottom: `3px solid ${theme.accent}`,
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px'
                }}>
                    <Coins size={isMobile ? 30 : 40} style={{ color: theme.accent }} />
                    <h3 style={{
                        color: theme.gold,
                        fontWeight: 'bolder',
                        textTransform: 'uppercase',
                        margin: '0',
                        fontSize: isMobile ? '24px' : '32px'
                    }}>
                        Highest Balance
                    </h3>
                </div>
                <div style={{ padding: isMobile ? '17px' : '26px' }}>
                    {leaderboard.top_by_balance.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            color: theme.accent,
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            padding: isMobile ? '20px' : '30px',
                            fontSize: isMobile ? '18px' : '20px'
                        }}>
                            No data yet
                        </div>
                    ) : (
                        leaderboard.top_by_balance.map(entry => (
                            <LeaderboardRow
                                key={entry.session_id}
                                entry={entry}
                                sessionId={sessionId}
                                theme={theme}
                                isMobile={isMobile}
                                value={entry.balance ? `${entry.balance.toFixed(2)} SOL` : ''}
                            />
                        ))
                    )}
                </div>
            </div>

            {/* Best Win Rate */}
            <div style={{
                backgroundColor: theme.panelBg,
                borderRadius: '10px',
                boxShadow: '0 4px 7px rgba(40, 40, 40, 1)',
                animation: 'bubbleAppear 0.7s ease-in-out'
            }}>
                <div style={{
                    padding: isMobile ? '17px' : '26px',
                    borderBottom: `3px solid ${theme.accent}`,
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px'
                }}>
                    <TrendingUp size={isMobile ? 30 : 40} style={{ color: theme.accent }} />
                    <h3 style={{
                        color: theme.gold,
                        fontWeight: 'bolder',
                        textTransform: 'uppercase',
                        margin: '0',
                        fontSize: isMobile ? '24px' : '32px'
                    }}>
                        Best Win Rate
                    </h3>
                </div>
                <div style={{ padding: isMobile ? '17px' : '26px' }}>
                    {leaderboard.top_by_winrate.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            color: theme.accent,
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            padding: isMobile ? '20px' : '30px',
                            fontSize: isMobile ? '18px' : '20px'
                        }}>
                            No data yet (min 5 auctions)
                        </div>
                    ) : (
                        leaderboard.top_by_winrate.map(entry => (
                            <LeaderboardRow
                                key={entry.session_id}
                                entry={entry}
                                sessionId={sessionId}
                                theme={theme}
                                isMobile={isMobile}
                                value={entry.win_rate ? `${(entry.win_rate * 100).toFixed(1)}%` : ''}
                            />
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

const LeaderboardRow = ({ entry, sessionId, theme, isMobile, value }) => (
    <div style={{
        backgroundColor: entry.session_id === sessionId ? theme.gold : theme.cardBg,
        borderRadius: '5px',
        boxShadow: '0 4px 7px rgba(40, 40, 40, 1)',
        padding: isMobile ? '17px' : '20px',
        marginBottom: isMobile ? '12px' : '17px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '17px' }}>
            <div style={{
                width: isMobile ? '40px' : '50px',
                height: isMobile ? '40px' : '50px',
                borderRadius: '50%',
                backgroundColor: entry.rank === 1 ? '#FFD700' : entry.rank === 2 ? '#C0C0C0' : entry.rank === 3 ? '#CD7F32' : '#666',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bolder',
                fontSize: isMobile ? '20px' : '24px',
                color: 'white'
            }}>
                {entry.rank}
            </div>
            <div>
                <div style={{
                    color: entry.session_id === sessionId ? theme.statusBarText : theme.text,
                    fontWeight: 'bold',
                    fontSize: isMobile ? '18px' : '22px'
                }}>
                    {entry.display_name}
                </div>
                <div style={{
                    color: entry.session_id === sessionId ? theme.statusBarText : theme.text,
                    fontSize: isMobile ? '14px' : '16px',
                    opacity: '0.8'
                }}>
                    Level {entry.level}
                </div>
            </div>
        </div>
        <div style={{
            color: entry.session_id === sessionId ? theme.statusBarText : '#75bd4f',
            fontWeight: 'bolder',
            fontSize: isMobile ? '20px' : '28px'
        }}>
            {value}
        </div>
    </div>
);

export default Leaderboard;
